import { FirebaseApp, initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";

import { UserSystemRole } from "./enums/UserSystemRole";
import { Navigation } from "./navigation";
import { PageContext } from "./page-context";

import { LoginHandler } from "./login-handler";
import { CurrentUser } from "./models/CurrentUser";
import { showError } from "./common";
import { WhiteGuard } from "./utils/white-guard";

declare global {
    interface Window {
        testRole: UserSystemRole;
        isTest?: boolean;
        fireBaseLog: (eventName: string, eventData: any) => void;
    }
}

class App {
    isTest = false;
    fireApp!: FirebaseApp;
    firebase!: any;
    context!: PageContext;
    authentication!: LoginHandler;



    async initializeFireApp() {

        if (!this.isTest) {
            const firebaseConfig = await fetch("/config/firebase-config.json").then(response => response.json());
            this.fireApp = initializeApp(firebaseConfig);
            // Use multi-tab IndexedDb persistence.
            initializeFirestore(this.fireApp, {
                localCache: persistentLocalCache(/*settings*/{ tabManager: persistentMultipleTabManager() })
            });
            this.firebase = {
                analytics: getAnalytics(this.fireApp),
                logEvent: (eventName: string, eventData: any) => logEvent(this.firebase.analytics, eventName, { ...eventData, page_path: window.location.pathname })
            };
        }
    }

    initAppInTestMode() {
        this.context.store.hasBackend = false;
        this.context.userPreferences.hasBackend = false;

        window.fireBaseLog = function (eventName: string, eventData: any) { };

        this.context.store.loadMockData();
    }




    async initializeDataAfterLogin() {
        try {
            if (this.isTest) {
                this.context.user = new CurrentUser([], '', '');
                this.context.user.roles = [window.testRole];
                this.context.user.userId = "local";
                this.context.user.systemRole = window.testRole;
                this.context.authentication = this.authentication;
            }
            else {
                this.context.user = await this.authentication.getCurrentUser();
                this.context.authentication = this.authentication;
            }

            // Check whitelist access
            const hasAccess = await WhiteGuard.check(this.context.user, this.fireApp);
            if (!hasAccess) {
                this.hideLoading();
                return;
            }

            this.context.store.preLoadData();
            this.updateUserInfo(this.context.user);
            Navigation.hashNav();
        } catch (err) {
            $(".loader").hide();
            const error = err as Error;
            showError(error.message, true);
            console.error("error getting current user", err);
            this.hideLoading();
        }
    }

    async main() {

        try {

            await this.initializeFireApp();
            this.isTest = window.isTest ? window.isTest : false;
            if (this.isTest === true) {
                this.context = new PageContext(null as unknown as FirebaseApp);
                this.authentication = new LoginHandler(null as unknown as FirebaseApp);
                this.initAppInTestMode();
            } else {
                this.context = new PageContext(this.fireApp);
                this.authentication = new LoginHandler(this.fireApp);
            }

            globalApp.context = this.context;

            await this.initializeDataAfterLogin();
        } catch (err) {
            const error = err as Error;
            const errorMessage = error.message;
            console.error(error.stack, errorMessage);
            showError(error.message, true);
        }
    }
    /**
     * 
     * @param {CurrentUser} user 
     */
    updateUserInfo(user: CurrentUser) {
        let additionalRoleIcon = '';

        if (user.roles.includes(UserSystemRole.Admin)) {
            additionalRoleIcon = '<i class="bi bi-shield-fill" title="Role: Admin"></i>&nbsp;';
            this.addDataImportHandler();
        } else if (user.roles.includes(UserSystemRole.Planner)) {
            additionalRoleIcon = '<i class="bi bi-briefcase-fill" title="Role: Planner"></i>&nbsp;';
        } else if (user.roles.includes(UserSystemRole.ProjectManager)) {
            additionalRoleIcon = '<i class="bi bi-briefcase" title="Role: Project Manager"></i>&nbsp;';
        } else if (user.roles.includes(UserSystemRole.Lead)) {
            additionalRoleIcon = '<i class="bi bi-chat-left-text" title="Role: Lead"></i>&nbsp;';
        } else if (user.roles.includes(UserSystemRole.Commenter)) {
            additionalRoleIcon = '<i class="bi bi-chat-dots" title="Role: Commenter"></i>&nbsp;';
        }
        if (user.firebaseUser)
            $(".loggedin").html(additionalRoleIcon + user.firebaseUser.displayName);
        // if (user.firebaseUser && user.hasEditPermission()) {
        //     $(".navigation-header").append('<a class="nav-link" style="color:lightseagreen" onclick="hamburgerMenuToggler()" href="https://project-assignments-new-ui.web.app/" target="_blank">New UI</a>');
        // }
    }

    addDataImportHandler() {
        document.addEventListener('dragover', (e) => e.preventDefault());
        document.addEventListener('drop', (e) => {
            e.preventDefault();
            if (!confirm("Are you sure you want to import data from this file?")) return;
            if (!confirm("Data might be overwritten. Are you sure you want to import data from this file?")) return;
            const file = e.dataTransfer && e.dataTransfer.files[0];
            const reader = new FileReader();
            reader.onload = async (e) => {
                if (e.target && file) {
                    if (typeof e.target.result === 'string') {
                        const entities = JSON.parse(e.target.result);
                        const fileName = file.name.split(".")[0];
                        await this.context.store.setEntities(fileName, entities);
                    } else {
                        console.error("File content is not a string");
                    }
                }
            }
            if (file)
                reader.readAsText(file);
        });
    }

    showLoading() {
        $(".loader").show();
    }

    hideLoading() {
        $(".loader").hide();
    }

    globalErrorHandler(event: any) {
        var msg = event;
        if (event.originalEvent && event.originalEvent.message) {
            msg = event.originalEvent.message;
        }
        if (event.reason) {
            msg = event.reason;
        }
        if (event.message) {
            msg = event.message;
        }
        showError(msg);
        return true;
    }
}

interface MyGlobal {
    app: App;
    context: PageContext;
}

declare global {
    var globalApp: MyGlobal & typeof globalThis;
}

// Create the variable we'll export
const globalApp = window as unknown as MyGlobal & typeof globalThis;

// Also set it on the global scope if needed
(window as any).globalApp = globalApp;

// Initialize the app
const app = new App();
document.addEventListener("DOMContentLoaded", () => app.main());
window.addEventListener("hashchange", Navigation.hashNav);
window.addEventListener("unhandledrejection", app.globalErrorHandler);
window.addEventListener("error", app.globalErrorHandler);

globalApp.app = app; // For debugging

export { globalApp };

